// src/layouts/BottomDrawer/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Drawer, Grid, Segmented, Spin, Tabs, theme } from 'antd';
import {
  UpOutlined,
  DownOutlined,
  CalendarOutlined,
  MessageOutlined,
  PhoneOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import translateX from 'utils/translateX';
import useZustand from 'hooks/useZustand';
import app from 'configs/appResolver';
import { AVAILABLE_RESOURCES } from 'constants/Resources';
import accountService from 'modules/app-views/account/service';
import MuralTab from 'layouts/BottomDrawer/tabs/MuralTab';
import AgendaTab from 'layouts/BottomDrawer/tabs/AgendaTab';
import LeadsTab from 'layouts/BottomDrawer/tabs/LeadsTab';
import VoipTab from 'layouts/BottomDrawer/tabs/VoipTab';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const PILL_BASE = {
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 1001,
  cursor: 'pointer',
  padding: '6px 20px',
  borderRadius: '8px 8px 0 0',
  background: 'var(--ant-color-bg-elevated)',
  border: '1px solid var(--ant-color-border)',
  borderBottom: 'none',
  boxShadow: '0 -2px 8px rgba(0,0,0,0.08)',
  clipPath: 'inset(-20px -20px 0 -20px)',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  userSelect: 'none',
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const BottomDrawer = () => {
  // ---------------------------------------------------------------------------
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ---------------------------------------------------------------------------
  const { token } = theme.useToken();
  const storedCurrentPage = useZustand('module', 'currentPage');
  const storedSubscription = useZustand('auth', 'subscription');
  const storedModule = useZustand('module', 'config');

  // ---------------------------------------------------------------------------
  // 2. HOOKS & REFS
  // ---------------------------------------------------------------------------
  const navigate = useNavigate();

  // ---------------------------------------------------------------------------
  // 3. LOCAL VARIABLES I
  // ---------------------------------------------------------------------------
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const showDrawer = !!storedCurrentPage?.showMyTasksLink;
  // isCrmModule: show the Leads tab only when the active module is CRM
  const isCrmModule = storedModule?.id === 'crm';
  // appHasVoip: the VoIP resource exists in AVAILABLE_RESOURCES for the current app AND environment
  const currentEnv = app.HOMOLOGATION_MODE ? 'homologation' : 'production';
  const appHasVoip = AVAILABLE_RESOURCES.some(
    r => r.uid === 'moduleresources_voip_goto' && r.availability?.[app.ID]?.includes(currentEnv)
  );
  // voipActivated: the user's subscription actually includes the VoIP resource (enable full tab)
  const voipActivated = storedSubscription?.resources?.includes('moduleresources_voip_goto');
  // isOwner: current user is the subscription owner (can activate resources)
  const isOwner = storedSubscription?.profile === 'owner';

  // ---------------------------------------------------------------------------
  // 4. LOCAL STATE
  // ---------------------------------------------------------------------------
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(
    () => localStorage.getItem('bottomDrawerActiveTab') || 'mural'
  );
  const [muralReady, setMuralReady] = useState(false);
  const [agendaReady, setAgendaReady] = useState(false);
  const [leadsReady, setLeadsReady] = useState(false);
  const [voipReady, setVoipReady] = useState(false);

  // ---------------------------------------------------------------------------
  // 5. EFFECTS
  // ---------------------------------------------------------------------------
  // Guard: if VoIP resource is not available for this app while voip tab is active, reset to mural.
  // Only runs after subscription is loaded to avoid resetting on initial hydration.
  useEffect(() => {
    if (storedSubscription && !appHasVoip && activeTab === 'voip') {
      setActiveTab('mural');
      localStorage.setItem('bottomDrawerActiveTab', 'mural');
    }
  }, [storedSubscription, appHasVoip, activeTab]);

  // Guard: if the module changes away from CRM while leads tab is active, reset to mural.
  useEffect(() => {
    if (!isCrmModule && activeTab === 'leads') {
      setActiveTab('mural');
      localStorage.setItem('bottomDrawerActiveTab', 'mural');
    }
  }, [isCrmModule, activeTab]);

  // ---------------------------------------------------------------------------
  // 6. HANDLERS
  // ---------------------------------------------------------------------------
  const handleTabChange = key => {
    setActiveTab(key);
    localStorage.setItem('bottomDrawerActiveTab', key);
  };

  const handleMuralReady = useCallback(() => setMuralReady(true), []);
  const handleAgendaReady = useCallback(() => setAgendaReady(true), []);
  const handleLeadsReady = useCallback(() => setLeadsReady(true), []);
  const handleVoipReady = useCallback(() => setVoipReady(true), []);
  const handleGoToSubscriptions = useCallback(() => {
    setOpen(false);
    accountService.navigateToAccountSettings({ navigate, tab: 'subscriptions' });
  }, [navigate]);

  // ---------------------------------------------------------------------------
  // 7. LOCAL VARIABLES II
  // ---------------------------------------------------------------------------
  // Only wait for optional tabs when they are actually rendered
  const isLoading =
    !muralReady ||
    !agendaReady ||
    (isCrmModule && !leadsReady) ||
    (appHasVoip && voipActivated && !voipReady);

  // ---------------------------------------------------------------------------
  // 8. UI
  // ---------------------------------------------------------------------------
  const pillLabel = (
    <span
      className="text-uppercase"
      style={{
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: 0.5,
        marginTop: 6,
        marginBottom: 4,
        color: token.colorPrimary,
      }}
    >
      {translateX('workspace')}
    </span>
  );

  const pillClosedStyle = {
    ...PILL_BASE,
    position: 'fixed',
    bottom: 0,
  };

  const pillInsideStyle = {
    ...PILL_BASE,
    position: 'absolute',
    top: 0,
    transform: 'translate(-50%, -100%)',
  };

  if (!showDrawer) return null;

  return (
    <>
      <style>{`
        .bottom-drawer-tabs {
          flex: 1;
          min-height: 0;
          overflow: hidden;
        }
        .bottom-drawer-tabs > .ant-tabs-nav {
          display: none;
        }
        .bottom-drawer-tabs > .ant-tabs-content-holder {
          flex: 1;
          overflow: hidden;
        }
        .bottom-drawer-tabs .ant-tabs-content {
          height: 100%;
        }
        .bottom-drawer-tabs .ant-tabs-tabpane-active {
          height: 100%;
          overflow: hidden;
        }
        .bottom-drawer-tab-content {
          height: 100%;
          overflow-y: auto;
          padding: 12px;
        }
      `}</style>

      {/* Static pill at bottom - only when closed */}
      {!open && (
        <div style={pillClosedStyle} onClick={() => setOpen(true)}>
          <UpOutlined style={{ fontSize: 12, color: token.colorPrimary }} />
          {pillLabel}
        </div>
      )}

      {/* Bottom drawer */}
      <Drawer
        placement="bottom"
        size={isMobile ? 'large' : 'default'}
        open={open}
        onClose={() => setOpen(false)}
        closable={false}
        destroyOnHidden={false}
        styles={{
          body: { padding: '16px 12px 0', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
          wrapper: { overflow: 'visible', height: isMobile ? '85vh' : '79vh' },
        }}
      >
        {/* Pill inside drawer - moves physically with it */}
        <div style={pillInsideStyle} onClick={() => setOpen(false)}>
          <DownOutlined style={{ fontSize: 12, color: token.colorPrimary }} />
          {pillLabel}
        </div>
        {/* Loading overlay */}
        {isLoading && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'var(--ant-color-bg-elevated)',
            }}
          >
            <Spin size="large"> </Spin>
          </div>
        )}

        <Card
          style={{ flex: 1, overflow: 'hidden', minHeight: 0 }}
          styles={{ body: { padding: 0, height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' } }}
        >
          <div style={{ flexShrink: 0, padding: '24px 12px 4px', display: 'flex', justifyContent: 'center' }}>
            <Segmented
              block
              value={activeTab}
              onChange={handleTabChange}
              options={buildSegmentedOptions(isCrmModule, appHasVoip)}
              style={{ maxWidth: isMobile ? undefined : 350, width: isMobile ? '100%' : 350 }}
            />
          </div>
          <Tabs
            tabPlacement="top"
            activeKey={activeTab}
            onChange={handleTabChange}
            rootClassName="tab-in-form bottom-drawer-tabs"
            items={buildTabItems({
              isCrmModule,
              appHasVoip,
              voipActivated,
              isOwner,
              handleMuralReady,
              handleAgendaReady,
              handleLeadsReady,
              handleVoipReady,
              handleGoToSubscriptions,
              voipActive: open && activeTab === 'voip',
            })}
            animated={{ inkBar: true, tabPane: false }}
            destroyOnHidden={false}
          />
        </Card>
      </Drawer>
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default BottomDrawer;

function buildSegmentedOptions(isCrmModule, appHasVoip) {
  const options = [
    { value: 'mural', icon: <MessageOutlined />, label: translateX('mural') },
    { value: 'agenda', icon: <CalendarOutlined />, label: translateX('agenda') },
  ];
  if (isCrmModule) {
    options.push({ value: 'leads', icon: <TeamOutlined />, label: translateX('leads') });
  }
  if (appHasVoip) {
    options.push({ value: 'voip', icon: <PhoneOutlined />, label: translateX('goto_voip_tab') });
  }
  return options;
}

// *******************************************************************************
// HELPERS
// *******************************************************************************
function buildTabItems({
  isCrmModule,
  appHasVoip,
  voipActivated,
  isOwner,
  handleMuralReady,
  handleAgendaReady,
  handleLeadsReady,
  handleVoipReady,
  handleGoToSubscriptions,
  voipActive,
}) {
  const items = [
    {
      key: 'mural',
      icon: <MessageOutlined />,
      label: <span>{translateX('mural')}</span>,
      forceRender: true,
      children: (
        <div className="bottom-drawer-tab-content">
          <MuralTab onReady={handleMuralReady} />
        </div>
      ),
    },
    {
      key: 'agenda',
      icon: <CalendarOutlined />,
      label: <span>{translateX('agenda')}</span>,
      forceRender: true,
      children: (
        <div className="bottom-drawer-tab-content">
          <AgendaTab onReady={handleAgendaReady} />
        </div>
      ),
    },
  ];

  if (isCrmModule) {
    items.push({
      key: 'leads',
      icon: <TeamOutlined />,
      label: <span>{translateX('leads')}</span>,
      forceRender: true,
      children: (
        <div className="bottom-drawer-tab-content">
          <LeadsTab onReady={handleLeadsReady} />
        </div>
      ),
    });
  }

  if (appHasVoip) {
    items.push({
      key: 'voip',
      icon: <PhoneOutlined />,
      label: <span>{translateX('goto_voip_tab')}</span>,
      forceRender: true,
      children: (
        <div className="bottom-drawer-tab-content">
          {voipActivated ? (
            <VoipTab onReady={handleVoipReady} active={voipActive} />
          ) : (
            <Alert
              type="warning"
              title={translateX('voip_goto_not_activated')}
              description={
                isOwner ? (
                  <span>
                    {translateX('voip_not_activated_owner_prefix')}{' '}
                    <Button type="link" style={{ padding: 0 }} onClick={handleGoToSubscriptions}>
                      {translateX('voip_not_activated_click_here')}
                    </Button>
                  </span>
                ) : (
                  translateX('voip_not_activated_non_owner')
                )
              }
            />
          )}
        </div>
      ),
    });
  }

  return items;
}
